import { useEffect, useRef, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { hideBanner, isBannerHidden } from "@/lib/impersonationStorage";


export function ImpersonationBanner() {
  const { isImpersonating, impersonatingAs, switchBack } = useAuth();
  const [dismissed, setDismissed] = useState(
    () => isBannerHidden(),
  );

  const ref = useRef<HTMLDivElement>(null);
  const visible = isImpersonating && !dismissed;

  // The banner is pinned above everything; its height is shared as --impersonation-h
  // so pinned headers and the page start below it instead of sliding underneath.
  useEffect(() => {
    const root = document.documentElement;
    const el = ref.current;
    if (!visible || !el) return;
    const set = () => root.style.setProperty("--impersonation-h", `${el.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => { ro.disconnect(); root.style.removeProperty("--impersonation-h"); };
  }, [visible]);

  if (!visible) return null;

  const hide = () => {
    hideBanner();
    setDismissed(true);
  };

  // Caution-tape stripes on the edges; text sits on a solid gold strip so it stays readable.
  const tape = {
    backgroundImage:
      "repeating-linear-gradient(-45deg, hsl(var(--accent-gold)) 0 14px, hsl(var(--accent-gold-foreground)) 14px 28px)",
  };

  return (
    <div ref={ref} role="status" className="fixed inset-x-0 top-0 z-[70] py-1.5" style={tape}>
      <div className="bg-accent-gold text-accent-gold-foreground px-3 py-1.5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-sm">
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="font-semibold text-center">
          <span className="uppercase tracking-wide font-extrabold">Caution:</span> You are viewing as{" "}
          <strong>{impersonatingAs || "another user"}</strong>!
        </span>
        <Button
          size="sm"
          className="h-8 min-h-[44px] sm:min-h-0 text-xs font-semibold bg-accent-gold-foreground text-accent-gold hover:bg-accent-gold-foreground/85 hover:text-accent-gold"
          onClick={switchBack}
        >
          Switch Back
        </Button>
        <Button
          size="icon"
          variant="ghost"
          aria-label="Hide banner"
          title="Hide until you switch back"
          className="h-11 w-11 sm:h-8 sm:w-8 shrink-0 text-accent-gold-foreground hover:bg-accent-gold-foreground/10 hover:text-accent-gold-foreground"
          onClick={hide}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
